import React from "react"
import PropTypes from "prop-types"
import dayjs from "dayjs"
import { DatePicker, Flex, TimePicker } from "antd"
import { useTranslation } from "react-i18next"
import { Text } from "ui/Typography"
import { Icon } from "ui/Icon"
import { DATE_DISPLAY_FORMAT_WITH_YEAR, DATE_VALUE_FORMAT, TIME_DISPLAY_FORMAT, TIME_VALUE_FORMAT } from "../../constants"
import { ENTRY_ERRORS, createEmptyEntry, formatTimeRange, isAllDayEntry, isRangeEntry } from "../../utils"
import styles from "./AppliesOnRows.module.css"

const ERROR_COPY = {
  [ENTRY_ERRORS.MISSING_DATE]: { key: "settings.blackout.error_missing_date", fallback: "Select a date, or remove this row" },
  [ENTRY_ERRORS.INCOMPLETE_TIME]: { key: "settings.blackout.error_incomplete_time", fallback: "Set both a start and an end time" },
  [ENTRY_ERRORS.TIME_ORDER]: { key: "settings.blackout.error_time_order", fallback: "End time must be after the start time" },
  [ENTRY_ERRORS.DUPLICATE]: { key: "settings.blackout.error_duplicate_entry", fallback: "These dates are already added above" },
  [ENTRY_ERRORS.OVERLAP]: { key: "settings.blackout.error_overlapping_entry", fallback: "These dates overlap a row above" }
}

const { RangePicker } = DatePicker

/**
 * The repeatable "Applies on" list. Each row is one entry and has three states:
 *  - date only, offering "+ Add Time Range"
 *  - date plus a time range, with an ✕ to clear back to all-day
 *  - a date *range* plus times, which adds a note clarifying that the times apply to
 *    every selected day rather than spanning the range continuously
 *
 * `isSingleDate` is set while the blackout repeats: the row is then the first day of the repeat,
 * one date and an optional time window, and the repeat's own "Ends" says when it stops. A range
 * would be a second, competing span ("a year-long block, every 2 weeks, ending in December"), so
 * the picker takes one date and "Add More" is hidden.
 */
const AppliesOnRows = ({ entries, onChange, entryErrors = {}, isSingleDate = false }) => {
  const { t } = useTranslation()

  const updateEntry = (entryId, changes) => onChange(entries.map((entry) => (entry.id === entryId ? { ...entry, ...changes } : entry)))

  const removeEntry = (entryId) => onChange(entries.filter((entry) => entry.id !== entryId))

  const addEntry = () => onChange([...entries, createEmptyEntry()])

  const handleDateChange = (entry, dates) => {
    if (!dates?.length || !dates[0]) {
      updateEntry(entry.id, { start_date: null, end_date: null })
      return
    }
    updateEntry(entry.id, {
      start_date: dates[0].format(DATE_VALUE_FORMAT),
      end_date: dates[1] ? dates[1].format(DATE_VALUE_FORMAT) : dates[0].format(DATE_VALUE_FORMAT)
    })
  }

  const toTimeValue = (time) => (time ? dayjs(time, TIME_VALUE_FORMAT) : null)

  // A blackout closes future availability, so a past date can never be chosen. Same rule as
  // Schedule Time Off. Dates already saved on an edited blackout still display.
  const disablePastDates = (current) => current?.isBefore(dayjs().startOf("day"))

  return (
    <div className={styles.wrapper}>
      <Text variant="sub" size="md" fw="500" className={styles.label}>
        {t("settings.blackout.applies_on", { defaultValue: "Applies on" })}
      </Text>
      <Text variant="caption" size="lg" className={styles.hint}>
        {isSingleDate
          ? t("settings.blackout.applies_on_hint_repeat", { defaultValue: "Select the first date. Repeat sets when it happens again." })
          : t("settings.blackout.applies_on_hint", { defaultValue: "Select dates or date ranges" })}
      </Text>

      <div className={styles.rows}>
        {entries.map((entry) => {
          const hasTimeRange = !isAllDayEntry(entry)
          const error = ERROR_COPY[entryErrors[entry.id]]
          // The note explains a valid range; an invalid one gets the error instead of both.
          const showsPerDayNote = !error && hasTimeRange && isRangeEntry(entry) && entry.start_time && entry.end_time

          return (
            <div key={entry.id} className={styles.row}>
              <Flex align="center" gap="var(--spacing-s)" wrap className={styles.controls}>
                {isSingleDate ? (
                  <DatePicker
                    className={styles.datePicker}
                    placeholder={t("settings.blackout.select_date", { defaultValue: "Select date" })}
                    format={DATE_DISPLAY_FORMAT_WITH_YEAR}
                    disabledDate={disablePastDates}
                    value={entry.start_date ? dayjs(entry.start_date) : null}
                    onChange={(date) => handleDateChange(entry, date ? [date, date] : null)}
                  />
                ) : (
                  <RangePicker
                    allowEmpty={[false, true]}
                    className={styles.datePicker}
                    placeholder={[t("settings.blackout.select_date", { defaultValue: "Select date" }), ""]}
                    format={DATE_DISPLAY_FORMAT_WITH_YEAR}
                    disabledDate={disablePastDates}
                    value={entry.start_date ? [dayjs(entry.start_date), entry.end_date ? dayjs(entry.end_date) : null] : null}
                    onChange={(dates) => handleDateChange(entry, dates)}
                  />
                )}

                {hasTimeRange ? (
                  <Flex align="center" gap="var(--spacing-xs)" className={styles.timeGroup}>
                    <TimePicker
                      use12Hours
                      needConfirm={false}
                      format={TIME_DISPLAY_FORMAT}
                      className={styles.timePicker}
                      value={toTimeValue(entry.start_time)}
                      onChange={(time) => updateEntry(entry.id, { start_time: time ? time.format(TIME_VALUE_FORMAT) : null })}
                    />
                    <span className={styles.timeSeparator}>-</span>
                    <TimePicker
                      use12Hours
                      needConfirm={false}
                      format={TIME_DISPLAY_FORMAT}
                      className={styles.timePicker}
                      value={toTimeValue(entry.end_time)}
                      onChange={(time) => updateEntry(entry.id, { end_time: time ? time.format(TIME_VALUE_FORMAT) : null })}
                    />
                    <button
                      type="button"
                      className={styles.clearTimeButton}
                      aria-label={t("settings.blackout.clear_time_range", { defaultValue: "Clear time range" })}
                      onClick={() => updateEntry(entry.id, { start_time: null, end_time: null })}
                    >
                      <Icon name="X" width={20} height={20} color="var(--color-primary)" />
                    </button>
                  </Flex>
                ) : (
                  <button
                    type="button"
                    className={styles.addTimeButton}
                    onClick={() => updateEntry(entry.id, { start_time: "08:00", end_time: "17:00" })}
                  >
                    <Icon name="Plus" width={14} height={14} />
                    {t("settings.blackout.add_time_range", { defaultValue: "Add Time Range" })}
                  </button>
                )}

                <button
                  type="button"
                  className={styles.deleteButton}
                  aria-label={t("settings.blackout.remove_date", { defaultValue: "Remove date" })}
                  onClick={() => removeEntry(entry.id)}
                  disabled={entries.length === 1}
                >
                  <Icon name="Trash2" width={16} height={16} color="currentColor" />
                </button>
              </Flex>

              {error && (
                <Flex align="center" gap="var(--spacing-xxs)" className={styles.rowError}>
                  <Icon name="AlertTriangle" width={14} height={14} />
                  <Text variant="caption" size="lg" className={styles.rowErrorText}>
                    {t(error.key, { defaultValue: error.fallback })}
                  </Text>
                </Flex>
              )}

              {showsPerDayNote && (
                <Flex align="center" gap="var(--spacing-xxs)" className={styles.perDayNote}>
                  <Icon name="Info" width={14} height={14} />
                  <Text variant="caption" size="lg" className={styles.perDayNoteText}>
                    {t("settings.blackout.per_day_note", {
                      defaultValue: "Each selected day will be unavailable from {{range}}",
                      range: formatTimeRange(entry)
                    })}
                  </Text>
                </Flex>
              )}
            </div>
          )
        })}
      </div>

      {!isSingleDate && (
        <button type="button" className={styles.addMoreButton} onClick={addEntry}>
          <Icon name="Plus" width={14} height={14} />
          {t("settings.blackout.add_more", { defaultValue: "Add More" })}
        </button>
      )}
    </div>
  )
}

AppliesOnRows.propTypes = {
  entries: PropTypes.arrayOf(
    PropTypes.shape({
      // Client-generated for new rows, server-assigned for saved ones.
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      start_date: PropTypes.string,
      end_date: PropTypes.string,
      start_time: PropTypes.string,
      end_time: PropTypes.string
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  /** While repeating: one row, one date (see above). */
  isSingleDate: PropTypes.bool,
  /** Entry id -> one of `ENTRY_ERRORS`; rows absent from the map are valid. */
  entryErrors: PropTypes.objectOf(PropTypes.string)
}

export default AppliesOnRows
